import { Component, effect, inject, input, signal, untracked, ViewChild } from '@angular/core';
import { InventoryService, InventoryType } from '@servicios/inventory/inventory.service';
import { Column, FieldConfig } from '@sharedComponents/table-dynamic/interfaces';
import { ActionButton } from '@sharedComponents/table-dynamic/interfaces/actions-buttons.interface';
import { Roles } from '@modelos/interfaces/operation-result-types';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Validators } from '@angular/forms';
import { ButtonConfig } from '@sharedComponents/modal-template/modal-template.component';
import { TableDynamicComponent } from '@sharedComponents/table-dynamic/table-dynamic.component';

@Component({
  selector: 'app-table-inventory',
  templateUrl: './table-inventory.component.html',
  styleUrls: ['./table-inventory.component.css'],
  standalone: false
})
export class TableInventoryComponent {
  private service = inject(InventoryService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  
  @ViewChild(TableDynamicComponent) tableDynamic!: TableDynamicComponent;

  type = input.required<InventoryType>();

  loading = signal<boolean>(false);
  data = signal<any[]>([]);
  totalRecords = signal<number>(0);
  first = signal<number>(0);
  pageSize = signal<number>(10);
  cols = signal<Column[]>([]);
  actionButtons = signal<ActionButton[]>([]);

  showModal = signal(false);
  selectedItem = signal<any | null>(null);
  fieldConfig = signal<FieldConfig[]>([]);
  buttonsConfig = signal<ButtonConfig[]>([]);
  
  // Ordenamiento
  sortField = signal<string>('id');
  sortOrder = signal<number>(1);

  constructor() {
    effect(() => {
      // Capture el valor del input signal para rastrear cambios
      const currentType = this.type();
      
      untracked(() => {
        // Limpiar estado previo para evitar que se vea información vieja
        this.data.set([]);
        this.totalRecords.set(0);
        this.first.set(0);
        
        // Cargar nueva configuración y datos
        this.loadConfig();
        this.loadData().catch(error => {
          console.error(`Error loading data for ${currentType}:`, error);
        });
      });
    });
  }

  loadConfig() {
    const type = this.type();
    let columns: Column[] = [
      { field: 'id', header: 'ID', visible: false },
      { field: 'v_', header: 'Version', visible: false },
    ];

    if (type === 'supplier') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'internal_code', header: 'Código Interno', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'form') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'regulations') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'controller_organization', header: 'Org. Controlador', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'brand') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'location') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'usage-purpose') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'waste-type') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'cost-concepts') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'study-tags') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'studies') {
      columns.push(
        { field: 'code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'status', header: 'Estado', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } }
      );
    } else if (type === 'formulation-types') {
      columns.push(
        { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'code', header: 'Abreviatura / Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'classification', header: 'Clasificación', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'study_suffix', header: 'Sufijo BPL', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
        { field: 'allow_multiple_actives', header: 'Múltiples Activos', visible: true, type: 'boolean', filter: { enable: false, type: 'boolean' } }
      );
    }

    this.cols.set(columns);

    this.actionButtons.set([
      {
        icon: 'pi pi-pencil',
        label: 'Editar',
        type: 'edit',
        roles: [Roles.ADMINISTRATIVE, Roles.ADMIN],
        action: (event: any) => this.edit(event)
      },
      {
        icon: 'pi pi-trash',
        label: 'Eliminar',
        type: 'delete',
        roles: [Roles.ADMINISTRATIVE, Roles.ADMIN],
        action: (event: any) => this.confirmDelete(event)
      }
    ]);
  }

  setFieldConfig() {
    const type = this.type();
    const config: FieldConfig[] = [
      { key: 'description', label: 'Descripción', type: 'textarea', placeholder: 'Ingrese una descripción', visible: true }
    ];

      if (type === 'supplier') {
      config.unshift(
        { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese el nombre del proveedor', validators: [Validators.required], required: true, visible: true },
        { key: 'internal_code', label: 'Código Interno', type: 'text', placeholder: 'Ingrese el código interno', validators: [Validators.required], required: true, visible: true }
      );
    } else if (type === 'form') {
      config.unshift(
        { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese el nombre', validators: [Validators.required], required: true, visible: true },
        { key: 'code', label: 'Código', type: 'text', placeholder: 'Ingrese el código', validators: [Validators.required], required: true, visible: true }
      );
    } else if (type === 'regulations') {
      config.unshift(
        { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese el nombre de la regulación', validators: [Validators.required], required: true, visible: true },
        { key: 'code', label: 'Código', type: 'text', placeholder: 'Ingrese el código', validators: [Validators.required], required: true, visible: true },
        { key: 'controller_organization', label: 'Organización Controladora', type: 'text', placeholder: 'Ingrese la organización', visible: true }
      );
    } else if (type === 'brand') {
      config.unshift(
        { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese el nombre de la marca', validators: [Validators.required], required: true, visible: true },
        { key: 'code', label: 'Código', type: 'text', placeholder: 'Ingrese el código', validators: [Validators.required], required: true, visible: true }
      );
    } else if (type === 'location') {
      config.unshift(
        { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese el nombre de la ubicación', validators: [Validators.required], required: true, visible: true },
        { key: 'code', label: 'Código', type: 'text', placeholder: 'Ingrese el código de ubicación', validators: [Validators.required], required: true, visible: true }
      );
    } else if (type === 'usage-purpose') {
      config.unshift(
        { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese el nombre del propósito', validators: [Validators.required], required: true, visible: true },
        { key: 'code', label: 'Código', type: 'text', placeholder: 'Ingrese el código del propósito', validators: [Validators.required], required: true, visible: true }
      );
    } else if (type === 'waste-type') {
      config.unshift(
        { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese el nombre del tipo de residuo', validators: [Validators.required], required: true, visible: true },
        { key: 'code', label: 'Código', type: 'text', placeholder: 'Ingrese el código del tipo de residuo', validators: [Validators.required], required: true, visible: true }
      );
    } else if (type === 'cost-concepts') {
      config.unshift(
        { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese el nombre del concepto de costo', validators: [Validators.required], required: true, visible: true },
        { key: 'code', label: 'Código', type: 'text', placeholder: 'Ingrese el código del concepto de costo', validators: [Validators.required], required: true, visible: true }
      );
    } else if (type === 'study-tags') {
      config.unshift(
        { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese el nombre de la sigla', validators: [Validators.required], required: true, visible: true },
        { key: 'code', label: 'Código', type: 'text', placeholder: 'Ingrese el código de la sigla', validators: [Validators.required], required: true, visible: true }
      );
    } else if (type === 'studies') {
      config.unshift(
        { key: 'code', label: 'Código', type: 'text', placeholder: 'Código del estudio (Autogenerado o Manual)', validators: [Validators.required], required: true, visible: true },
        { key: 'status', label: 'Estado', type: 'text', placeholder: 'OPEN / CLOSED', validators: [Validators.required], required: true, visible: true }
      );
    } else if (type === 'formulation-types') {
      config.unshift(
        { key: 'name', label: 'Nombre Completo', type: 'text', placeholder: 'Ej: Grado Técnico', validators: [Validators.required], required: true, visible: true },
        { key: 'code', label: 'Abreviatura / Código', type: 'text', placeholder: 'Ej: GT', validators: [Validators.required], required: true, visible: true },
        { key: 'classification', label: 'Clasificación', type: 'select', placeholder: 'Seleccione clasificación...', options: [{label: 'Grado Técnico / Técnico Concentrado', value: 'Grado Técnico / Técnico Concentrado'}, {label: 'Formulados', value: 'Formulados'}], validators: [Validators.required], required: true, visible: true },
        { key: 'study_suffix', label: 'Sufijo BPL (Opcional)', type: 'text', placeholder: 'Ej: IA (para Grado Técnico)', visible: true },
        { key: 'allow_multiple_actives', label: 'Permite Múltiples Activos', type: 'switch', visible: true }
      );
    }

    this.fieldConfig.set(config);
    this.buttonsConfig.set([
      { label: 'Cancelar', icon: 'pi pi-times', type: 'cancel', action: () => this.hideModal() },
      { label: 'Guardar', icon: 'pi pi-check', type: 'save', action: () => { } }
    ]);
  }

  async loadData(event?: any) {
    try {
      this.loading.set(true);
      if (event?.first !== undefined) {
        this.first.set(event.first);
      }
      if (event?.sortField) {
        this.sortField.set(event.sortField);
      }
      if (event?.sortOrder !== undefined) {
        this.sortOrder.set(event.sortOrder);
      }
      if (event?.rows !== undefined) {
        this.pageSize.set(event.rows);
      }
      const res = await this.service.getAll(this.type(), {
        page: (this.first() / this.pageSize()) + 1 || 1,
        limit: this.pageSize(),
        search: event?.globalFilter || '',
        sortBy: this.sortField(),
        sortOrder: this.sortOrder() > 0 ? 'asc' : 'desc',
        filters: event?.filters
      });
      this.data.set(res.data);
      this.totalRecords.set(res.total);
      this.loading.set(false);
    } catch (error) {
      console.error(error);
      this.loading.set(false);
    }
  }

  edit(item: any) {
    this.selectedItem.set({ ...item });
    this.setFieldConfig();
    this.showModal.set(true);
  }

  newItem() {
    this.selectedItem.set({});
    this.setFieldConfig();
    this.showModal.set(true);
  }

  confirmDelete(item: any) {
    this.confirmationService.confirm({
      message: `¿Está seguro de que desea eliminar este registro?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.delete(item),
      acceptButtonStyleClass: 'p-button-danger'
    });
  }

  async delete(item: any) {
    try {
      this.loading.set(true);
      await this.service.delete(this.type(), item.id, item.v_);
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Registro eliminado correctamente' });
      this.tableDynamic?.clearSelection();
      this.loadData();
    } catch (error: any) {
      console.error(error);
      const detail = error.error?.message || error.message || 'No se pudo eliminar el registro';
      this.messageService.add({ severity: 'error', summary: 'Error', detail });
      this.loading.set(false);
    }
  }

  confirmDeleteSelected(items: any[]) {
    this.confirmationService.confirm({
      message: `¿Está seguro de que desea eliminar ${items.length} registros seleccionados?`,
      header: 'Confirmar eliminación múltiple',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteSelected(items),
      acceptButtonStyleClass: 'p-button-danger'
    });
  }

  async deleteSelected(items: any[]) {
    try {
      this.loading.set(true);
      const itemsToDelete = items.map(i => ({ id: i.id, v_: i.v_ }));
      await this.service.deleteMany(this.type(), itemsToDelete);
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Registros eliminados correctamente' });
      this.tableDynamic?.clearSelection();
      this.loadData();
    } catch (error: any) {
      console.error(error);
      const detail = error.error?.message || error.message || 'No se pudieron eliminar los registros';
      this.messageService.add({ severity: 'error', summary: 'Error', detail });
      this.loading.set(false);
    }
  }

  hideModal() {
    this.showModal.set(false);
    this.selectedItem.set(null);
  }

  async save(data: any) {
    try {
      this.loading.set(true);
      const type = this.type();
      
      // Filter payload to include ONLY what each specific backend DTO expects
      // This is crucial because forbidNonWhitelisted: true in the API Gateway 
      // rejects any extra fields like relationships or automated timestamps.
      let cleanPayload: any = {
        name: data.name,
        description: data.description || '',
      };

      // Type-specific field mapping
      if (type === 'supplier') {
        cleanPayload.internal_code = data.internal_code || data.code;
      } else if (type === 'studies') {
        cleanPayload = {
          code: data.code,
          status: data.status || 'OPEN',
          description: data.description || ''
        };
      } else if (type === 'formulation-types') {
        cleanPayload.code = data.code;
        cleanPayload.study_suffix = data.study_suffix;
        cleanPayload.classification = data.classification;
        cleanPayload.allow_multiple_actives = data.allow_multiple_actives;
      } else {
        cleanPayload.code = data.code;
      }

      // Remove active logic

      if (type === 'regulations') {
        cleanPayload.controller_organization = data.controller_organization || '';
      }

      if (data.id) {
        // All master entities now expect id and v_ in the body for PUT
        cleanPayload.id = data.id;
        // Ensure v_ is a Number and fetched from the original selected item
        cleanPayload.v_ = this.selectedItem()?.v_ !== undefined ? Number(this.selectedItem().v_) : 0;
        
        await this.service.update(type, data.id, cleanPayload);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Registro actualizado correctamente' });
      } else {
        await this.service.create(type, cleanPayload);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Registro creado correctamente' });
      }
      this.hideModal();
      this.loadData();
    } catch (error: any) {
      console.error('Error saving inventory item:', error);
      const detail = error.error?.message || error.message || 'No se pudo guardar el registro. Verifique los datos e intente nuevamente.';
      this.messageService.add({ severity: 'error', summary: 'Error', detail });
    } finally {
      this.loading.set(false);
    }
  }
}
